import { useState, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type ExpandedState,
  type SortingState,
  type ColumnFiltersState,
  type FilterFn,
} from '@tanstack/react-table';
import { ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, Layers, Package, BookOpen, CheckSquare, User, Calendar, Bug, X, FilterX } from 'lucide-react';
import clsx from 'clsx';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { useAuthStore } from '../../store/useAuthStore';
import { CheckoutBadge, CheckoutFilterSelect, matchesCheckout, type CheckoutFilter } from '../workitems/CheckoutBadge';
import { useConfigStore } from '../../store/useConfigStore';
import { useAppStore } from '../../store/useAppStore';
import { itemRef } from '../../utils/itemRef';
import type { WorkItem } from '../../types';

interface TreeItem extends WorkItem {
  subRows?: TreeItem[];
}

const typeIcons: Record<string, typeof Layers> = {
  epic: Layers,
  feature: Package,
  story: BookOpen,
  task: CheckSquare,
  bug: Bug,
};

const columnHelper = createColumnHelper<TreeItem>();

/** Custom multi-value filter function — supports array of allowed values */
const multiValueFilter: FilterFn<TreeItem> = (row, columnId, filterValue) => {
  const cellValue = row.getValue<string>(columnId);
  if (Array.isArray(filterValue)) {
    return filterValue.length === 0 || filterValue.includes(cellValue);
  }
  return cellValue === filterValue;
};

/** Friendly column label map */
const columnLabels: Record<string, string> = {
  key: 'ID',
  displayId: 'Badge',
  title: 'Title',
  status: 'Status',
  workItemType: 'Type',
  parentId: 'Parent',
  priority: 'Priority',
  assignee: 'Assignee',
  dueDate: 'Due Date',
  estimatedHours: 'Est.',
  loggedHours: 'Logged',
};

export default function TaskListView() {
  const { items: allItems } = useWorkItemStore();
  const me = useAuthStore((s) => s.user?.email?.toLowerCase());
  const [checkoutFilter, setCheckoutFilter] = useState<CheckoutFilter>('');
  // Checked out by me / by anyone / not checked out (EP-28): an item kept by the filter whose
  // parent is filtered out is shown at the top level.
  const items = useMemo(() => allItems.filter((i) => matchesCheckout(i.checkout, checkoutFilter, me)),
    [allItems, checkoutFilter, me]);
  const { getWorkItemTypeConfig, getStatusColor, getStatusLabel, getPriorityColor } = useConfigStore();
  const { setDetailItemId } = useAppStore();
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  /** Toggle a value in a multi-value column filter (array-based) */
  const addFilter = useCallback((columnId: string, value: string) => {
    setColumnFilters((prev) => {
      const existing = prev.find((f) => f.id === columnId);
      if (!existing) {
        // New filter — create with single value in array
        return [...prev, { id: columnId, value: [value] }];
      }
      const arr = Array.isArray(existing.value) ? (existing.value as string[]) : [existing.value as string];
      if (arr.includes(value)) {
        // Toggle off — remove this value from the array
        const next = arr.filter((v) => v !== value);
        if (next.length === 0) return prev.filter((f) => f.id !== columnId); // remove entire filter
        return prev.map((f) => (f.id === columnId ? { ...f, value: next } : f));
      }
      // Toggle on — add this value to the array
      return prev.map((f) => (f.id === columnId ? { ...f, value: [...arr, value] } : f));
    });
  }, []);

  /** Remove a single value from a column filter, or the whole filter */
  const removeFilter = useCallback((columnId: string, value?: string) => {
    setColumnFilters((prev) => {
      if (!value) return prev.filter((f) => f.id !== columnId);
      return prev
        .map((f) => {
          if (f.id !== columnId) return f;
          const arr = Array.isArray(f.value) ? (f.value as string[]).filter((v) => v !== value) : [];
          if (arr.length === 0) return null;
          return { ...f, value: arr };
        })
        .filter(Boolean) as ColumnFiltersState;
    });
  }, []);

  // Build tree structure
  const treeData = useMemo(() => {
    const itemMap = new Map<string, TreeItem>();
    items.forEach((item) => {
      itemMap.set(item._id, { ...item, subRows: [] });
    });

    const roots: TreeItem[] = [];
    itemMap.forEach((item) => {
      if (item.parentId && itemMap.has(item.parentId)) {
        itemMap.get(item.parentId)!.subRows!.push(item);
      } else {
        roots.push(item);
      }
    });

    // Sort children by order
    const sortChildren = (items: TreeItem[]) => {
      items.sort((a, b) => a.order - b.order);
      items.forEach((item) => {
        if (item.subRows?.length) sortChildren(item.subRows);
      });
    };
    sortChildren(roots);
    return roots;
  }, [items]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'title',
        header: 'Title',
        cell: ({ row }) => {
          const item = row.original;
          const typeConfig = getWorkItemTypeConfig(item.workItemType);
          const Icon = typeIcons[item.workItemType] || CheckSquare;
          const depth = row.depth;

          return (
            <div
              className="flex items-center gap-2"
              style={{ paddingLeft: `${depth * 20}px` }}
            >
              {row.getCanExpand() ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    row.getToggleExpandedHandler()();
                  }}
                  className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ChevronRight
                    size={14}
                    className={clsx('transition-transform', row.getIsExpanded() && 'rotate-90')}
                  />
                </button>
              ) : (
                <span className="w-5" />
              )}
              <Icon size={14} style={{ color: typeConfig.color }} className="shrink-0" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  addFilter('workItemType', item.workItemType);
                }}
                className="px-1 py-0.5 text-[10px] font-medium rounded hover:underline cursor-pointer"
                style={{
                  backgroundColor: typeConfig.color + '15',
                  color: typeConfig.color,
                }}
                title={`Filter by type: ${typeConfig.label}`}
              >
                {typeConfig.label}
              </button>
              <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.title}</span>
              <CheckoutBadge checkout={item.checkout} compact />
            </div>
          );
        },
        size: 400,
      }),
      // Filters on `key`, not `displayId`. Filtering by `ST-3` matched every
      // third-story-under-a-feature in the project, which made the column look
      // broken; `PO46-828` matches exactly one item.
      columnHelper.accessor((row) => itemRef(row), {
        id: 'key',
        header: 'ID',
        cell: ({ getValue }) => {
          const id = getValue();
          if (!id) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                addFilter('key', id);
              }}
              className="text-xs font-mono text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              title={`Filter by ID: ${id}`}
            >
              {id}
            </button>
          );
        },
        filterFn: multiValueFilter,
        size: 120,
      }),
      columnHelper.accessor('workItemType', {
        header: 'Type',
        cell: ({ getValue }) => {
          const type = getValue();
          const typeConfig = getWorkItemTypeConfig(type);
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                addFilter('workItemType', type);
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer capitalize"
              title={`Filter by type: ${typeConfig.label}`}
            >
              {typeConfig.label}
            </button>
          );
        },
        filterFn: multiValueFilter,
        size: 80,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <div className="flex items-center gap-1.5">
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: getStatusColor(status) }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  addFilter('status', status);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                title={`Filter by status: ${getStatusLabel(status)}`}
              >
                {getStatusLabel(status)}
              </button>
            </div>
          );
        },
        filterFn: multiValueFilter,
        size: 120,
      }),
      columnHelper.accessor('parentId', {
        header: 'Parent',
        cell: ({ getValue }) => {
          const parentId = getValue();
          if (!parentId) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
          const parent = allItems.find((i) => i._id === parentId);
          if (!parent) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
          const parentTypeConf = getWorkItemTypeConfig(parent.workItemType);
          const PIcon = typeIcons[parent.workItemType] || CheckSquare;
          return (
            <div className="flex items-center gap-1 truncate">
              <PIcon size={11} style={{ color: parentTypeConf.color }} className="shrink-0" />
              <span className="text-xs text-gray-600 dark:text-gray-300 truncate">{parent.title}</span>
            </div>
          );
        },
        size: 160,
      }),
      columnHelper.accessor('priority', {
        header: 'Priority',
        cell: ({ getValue }) => {
          const priority = getValue();
          return (
            <div className="flex items-center gap-1.5">
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: getPriorityColor(priority) }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  addFilter('priority', priority);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer capitalize"
                title={`Filter by priority: ${priority}`}
              >
                {priority}
              </button>
            </div>
          );
        },
        filterFn: multiValueFilter,
        size: 100,
      }),
      columnHelper.accessor('assignee', {
        header: 'Assignee',
        cell: ({ getValue }) => {
          const assignee = getValue();
          if (!assignee) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
          return (
            <div className="flex items-center gap-1">
              <User size={12} className="text-gray-400 shrink-0" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  addFilter('assignee', assignee);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer truncate"
                title={`Filter by assignee: ${assignee}`}
              >
                {assignee}
              </button>
            </div>
          );
        },
        filterFn: multiValueFilter,
        size: 120,
      }),
      columnHelper.accessor('dueDate', {
        header: 'Due Date',
        cell: ({ getValue }) => {
          const date = getValue();
          if (!date) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
          const d = new Date(date);
          const isOverdue = d < new Date() && items.find((i) => i.dueDate === date)?.status !== 'done';
          return (
            <div className="flex items-center gap-1">
              <Calendar size={12} className={isOverdue ? 'text-red-400' : 'text-gray-400'} />
              <span className={clsx('text-xs', isOverdue ? 'text-red-500 font-medium' : 'text-gray-600 dark:text-gray-300')}>
                {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          );
        },
        size: 100,
      }),
      columnHelper.accessor('estimatedHours', {
        header: 'Est.',
        cell: ({ getValue }) => {
          const hours = getValue();
          return <span className="text-xs text-gray-500 dark:text-gray-400">{hours > 0 ? `${hours}h` : '—'}</span>;
        },
        size: 60,
      }),
      columnHelper.accessor('loggedHours', {
        header: 'Logged',
        cell: ({ getValue }) => {
          const hours = getValue();
          return <span className="text-xs text-gray-500 dark:text-gray-400">{hours > 0 ? `${hours}h` : '—'}</span>;
        },
        size: 60,
      }),
      columnHelper.accessor('storyPoints', {
        header: 'SP',
        cell: ({ getValue }) => {
          const sp = getValue();
          return <span className="text-xs text-gray-500 dark:text-gray-400">{sp > 0 ? sp : '—'}</span>;
        },
        size: 50,
      }),
      columnHelper.accessor('wsjfScore', {
        header: 'WSJF',
        cell: ({ getValue }) => {
          const score = getValue();
          return <span className="text-xs text-gray-500 dark:text-gray-400">{score > 0 ? score.toFixed(1) : '—'}</span>;
        },
        size: 60,
      }),
    ],
    [getWorkItemTypeConfig, getStatusColor, getStatusLabel, getPriorityColor, items, allItems, addFilter]
  );

  const table = useReactTable({
    data: treeData,
    columns,
    state: { expanded, sorting, columnFilters },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getSubRows: (row) => row.subRows,
    filterFromLeafRows: true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  /** Render sort indicator for a column */
  const SortIcon = ({ columnId }: { columnId: string }) => {
    const col = table.getColumn(columnId);
    if (!col?.getCanSort()) return null;
    const sorted = col.getIsSorted();
    if (sorted === 'asc') return <ChevronUp size={12} className="text-accent" />;
    if (sorted === 'desc') return <ChevronDown size={12} className="text-accent" />;
    return <ChevronsUpDown size={11} className="text-gray-300 dark:text-gray-600" />;
  };

  /** Friendly display value for a filter */
  const getFilterDisplayValue = (columnId: string, value: unknown): string => {
    const v = String(value);
    if (columnId === 'status') return getStatusLabel(v);
    if (columnId === 'workItemType') return getWorkItemTypeConfig(v as WorkItem['workItemType']).label;
    return v;
  };

  /** Flatten multi-value filters into individual chips */
  const filterChips = useMemo(() => {
    const chips: { columnId: string; value: string; display: string }[] = [];
    for (const f of columnFilters) {
      const values = Array.isArray(f.value) ? (f.value as string[]) : [f.value as string];
      for (const v of values) {
        chips.push({ columnId: f.id, value: v, display: getFilterDisplayValue(f.id, v) });
      }
    }
    return chips;
  }, [columnFilters, getStatusLabel, getWorkItemTypeConfig]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
        <CheckoutFilterSelect value={checkoutFilter} onChange={setCheckoutFilter} />
        {checkoutFilter && <span className="text-xs text-gray-500">{items.length} of {allItems.length}</span>}
      </div>
      {/* Filter Bar */}
      {filterChips.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mr-1">Filters:</span>
          {filterChips.map((chip) => (
            <span
              key={`${chip.columnId}:${chip.value}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20"
            >
              <span className="text-gray-500 dark:text-gray-400">{columnLabels[chip.columnId] || chip.columnId}:</span>
              <span>{chip.display}</span>
              <button
                onClick={() => removeFilter(chip.columnId, chip.value)}
                className="ml-0.5 hover:text-red-500 transition-colors"
                title="Remove filter"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            onClick={() => setColumnFilters([])}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Clear all filters"
          >
            <FilterX size={12} />
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  return (
                    <th
                      key={header.id}
                      className={clsx(
                        'px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider',
                        canSort && 'cursor-pointer select-none hover:text-accent transition-colors'
                      )}
                      style={{ width: header.getSize() }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && <SortIcon columnId={header.column.id} />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-gray-100 dark:border-gray-700 hover:bg-accent/5 cursor-pointer transition-colors"
                onClick={() => setDetailItemId(row.original._id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {table.getRowModel().rows.length === 0 && (
          <div className="flex items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <p className="text-sm">
              {columnFilters.length > 0
                ? 'No work items match the current filters.'
                : 'No work items yet. Click "Add Item" to create one.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
